import { useState, useEffect } from 'react'
import { SmartleadDiscoveryService } from '@/lib/smartlead-discovery-service'
import type { DiscoveryResult, StatusComparison, DiscrepancyReport, SyncPlan } from '@/lib/smartlead-discovery-types'

export interface UseSmartleadDiscoveryReturn {
  discoveryResult: DiscoveryResult | null
  statusComparison: StatusComparison[]
  discrepancyReport: DiscrepancyReport | null
  syncPlan: SyncPlan | null
  loading: boolean
  error: string | null
  lastDiscovery: string | null
  runDiscovery: () => Promise<void>
  refreshStatusComparison: () => Promise<void>
  refreshDiscrepancyReport: () => Promise<void>
  refreshSyncPlan: () => Promise<void>
}

export function useSmartleadDiscovery(): UseSmartleadDiscoveryReturn {
  const [discoveryResult, setDiscoveryResult] = useState<DiscoveryResult | null>(null)
  const [statusComparison, setStatusComparison] = useState<StatusComparison[]>([])
  const [discrepancyReport, setDiscrepancyReport] = useState<DiscrepancyReport | null>(null)
  const [syncPlan, setSyncPlan] = useState<SyncPlan | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastDiscovery, setLastDiscovery] = useState<string | null>(null)

  const discoveryService = new SmartleadDiscoveryService()

  const runDiscovery = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log('🔍 Starting full discovery...')
      
      const result = await discoveryService.discoverMailboxes()
      setDiscoveryResult(result)
      setLastDiscovery(new Date().toISOString())
      
      console.log('✅ Discovery completed successfully')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMessage)
      console.error('❌ Discovery failed:', errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const refreshStatusComparison = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log('🔄 Refreshing status comparison...')
      
      const comparison = await discoveryService.getStatusComparison()
      setStatusComparison(comparison)
      
      console.log('✅ Status comparison refreshed')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMessage)
      console.error('❌ Status comparison failed:', errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const refreshDiscrepancyReport = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log('📋 Refreshing discrepancy report...')
      
      const report = await discoveryService.getDiscrepancyReport()
      setDiscrepancyReport(report)
      
      console.log('✅ Discrepancy report refreshed')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMessage)
      console.error('❌ Discrepancy report failed:', errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const refreshSyncPlan = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log('📝 Refreshing sync plan...')
      
      const plan = await discoveryService.getSyncPlan()
      setSyncPlan(plan)
      
      console.log('✅ Sync plan refreshed')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMessage)
      console.error('❌ Sync plan failed:', errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Auto-run discovery on mount
  useEffect(() => {
    runDiscovery()
  }, [])

  return {
    discoveryResult,
    statusComparison,
    discrepancyReport,
    syncPlan,
    loading,
    error,
    lastDiscovery,
    runDiscovery,
    refreshStatusComparison,
    refreshDiscrepancyReport,
    refreshSyncPlan
  }
}
